import React, { useState } from 'react';
import { useChat } from '../hooks/useChat';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../hooks/useTheme';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ModelSelector } from './ModelSelector';
import { Sidebar, RetrievalSettings } from './Sidebar';
import { UserMenu } from './UserMenu';
import { UserNoticeModal } from './UserNoticeModal';
import { appConfig } from '../config/appConfig';
import models from '../config/models.json';

interface ChatContainerProps {
  apiUrl: string;
  onSignOut: () => void;
}

export const ChatContainer: React.FC<ChatContainerProps> = ({ apiUrl, onSignOut }) => {
  const knowledgeBases = appConfig.ai.knowledgeBases || [];
  const [selectedKB, setSelectedKB] = useState(
    appConfig.ai.defaultKnowledgeBase || knowledgeBases[0]?.id || ''
  );
  const [selectedModel, setSelectedModel] = useState(models[0]?.id || '');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNotice, setShowNotice] = useState(() => {
    return !localStorage.getItem('userNoticeAccepted');
  });
  const [settings, setSettings] = useState<RetrievalSettings>({
    numberOfResults: 5,
    searchType: 'HYBRID',
    scoreThreshold: 0
  });
  const [customSystemPrompt, setCustomSystemPrompt] = useState(() => {
    return localStorage.getItem('customSystemPrompt') || '';
  });
  const { messages, isLoading, sendMessage, clearChat } = useChat(apiUrl);
  const { user } = useAuth();
  const { isDark, toggleTheme } = useTheme();

  const handleAcceptNotice = () => {
    localStorage.setItem('userNoticeAccepted', 'true');
    setShowNotice(false);
  };

  const handleCustomSystemPromptChange = (prompt: string) => {
    setCustomSystemPrompt(prompt);
    if (prompt) {
      localStorage.setItem('customSystemPrompt', prompt);
    } else {
      localStorage.removeItem('customSystemPrompt');
    }
  };

  const handleSend = (query: string) => {
    if (query.trim() && selectedKB && selectedModel) {
      sendMessage(query, selectedKB, selectedModel, settings, customSystemPrompt || undefined);
    }
  };

  const handleFeedback = async (messageIndex: number, rating: 'thumbs_up' | 'thumbs_down', feedbackText?: string) => {
    const feedbackApiUrl = appConfig.aws.feedbackApiUrl;
    
    if (!feedbackApiUrl || feedbackApiUrl.includes('PLACEHOLDER')) {
      console.warn('Feedback API URL not configured - skipping feedback submission');
      return;
    }

    const message = messages[messageIndex];
    if (!message?.messageId) {
      console.error('Message ID not found');
      return;
    }

    const kbName = knowledgeBases.find(kb => kb.id === selectedKB)?.name || selectedKB;
    
    try {
      const { submitFeedback } = await import('../services/feedbackService');
      await submitFeedback({
        messageId: message.messageId,
        rating,
        feedbackText,
        conversationHistory: messages,
        feedbackMessageIndex: messageIndex,
        settings: {
          knowledgeBaseId: selectedKB,
          knowledgeBaseName: kbName,
          modelId: selectedModel,
          retrievalConfig: settings,
          customSystemPrompt: customSystemPrompt || undefined
        },
        feedbackApiUrl
      });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
      {showNotice && <UserNoticeModal onAccept={handleAcceptNotice} />}
      
      <Sidebar 
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        selectedKB={selectedKB}
        onKBChange={setSelectedKB}
        settings={settings}
        onSettingsChange={setSettings}
        customSystemPrompt={customSystemPrompt}
        onCustomSystemPromptChange={handleCustomSystemPromptChange}
      />
      
      <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              title="Settings"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{appConfig.display.appName}</h1>
          </div>
          <div className="flex items-center gap-3">
            {appConfig.features.showModelSelector && (
              <ModelSelector value={selectedModel} onChange={setSelectedModel} />
            )}
            <button
              onClick={clearChat}
              className="px-3 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              Clear
            </button>
            {user?.name && (
              <UserMenu 
                username={user.name} 
                onSignOut={onSignOut}
                isDark={isDark}
                onToggleTheme={toggleTheme}
              />
            )}
          </div>
        </div>
      </header>
      
      <MessageList messages={messages} onFeedback={handleFeedback} />
      <MessageInput onSend={handleSend} disabled={isLoading} />
    </div>
  );
};
